import { useCallback, useRef, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setCounterAnimationStarted,
  setCounterAnimationFinished,
  setCriticalAnimationPlaying,
  setIsAnimatedWin,
  setWinAnimationsPlaying,
  setFreeSpinCycleComplete,
  closeAllPopups,
  setSymbolPopupBlocked,
} from "../State/actions";
import {
  selectCurrentRound,
  selectWinningLines,
  selectFreeSpinActive,
  selectFreeSpinComplete,
  selectFreeSpinSpinning,
} from "../State/selectors";
import { SOUND_NAME } from "../sounds";
import { useAppActivity } from "../Contexts/AppActivityContext";
import { useReelRefs } from "../Contexts/ReelRefsContext";
import { GAME_LAYOUT } from "../settings";
import { symbolData } from "../SymbolData";
import { FEATURES_CONFIG } from "../featuresConfig";
import { soundManager } from "../SoundManager";

/**
 * Free Spin Win Controller
 *
 * Stripped-down mirror of useWinController targeting free spin reel refs.
 * Handles: show-all wins → dim → line cycling (infinite loop).
 * Trigger: freeSpinComplete (guarded by hasSpunRef).
 */
export const useFreeSpinWinController = () => {
  const {
    freeSpinReelRefs,
    freeSpinOverlayReelRefs,
    winLinesContainerRef,
    dimReelSetRef,
    globalLineWinTextRef,
    lineWinTextRef,
  } = useReelRefs();

  const dispatch = useDispatch();

  const currentRound = useSelector(selectCurrentRound);
  const winningLines = useSelector(selectWinningLines);
  const freeSpinActive = useSelector(selectFreeSpinActive);
  const freeSpinComplete = useSelector(selectFreeSpinComplete);
  const freeSpinSpinning = useSelector(selectFreeSpinSpinning);

  // --- Refs ---
  const currentTimeoutsRef = useRef([]);
  const isAnimatingRef = useRef(false);
  const animationCompletedRef = useRef(false);
  const isFirstAnimationRef = useRef(true);
  const hasSpunRef = useRef(false);
  const currentRoundRef = useRef(currentRound);
  const winningLinesRef = useRef(winningLines);
  const dispatchRef = useRef(dispatch);
  const prevWinPosSetRef = useRef(null);
  const masterSpineDurationRef = useRef(null);
  const animatedRoundsRef = useRef(new Set());
  const winningDataRef = useRef(null);
  const lineIdsRef = useRef([]);
  const currentLineIndexRef = useRef(0);
  const { isActive } = useAppActivity();
  const activityRef = useRef(true);

  const hasWins = useMemo(
    () => currentRound?.win && currentRound.win > 0,
    [currentRound?.win],
  );

  // --- Sync refs ---
  useEffect(() => {
    currentRoundRef.current = currentRound;
    winningLinesRef.current = winningLines;
    dispatchRef.current = dispatch;
    activityRef.current = isActive;
  }, [currentRound, winningLines, dispatch, isActive]);

  // --- Helpers ---
  const clearAllTimeouts = useCallback(() => {
    for (const id of currentTimeoutsRef.current) clearTimeout(id);
    currentTimeoutsRef.current = [];
  }, []);

  const getReelControllers = useCallback(() => {
    const reels = freeSpinReelRefs.current;
    if (!reels) return [];
    return reels
      .map((reel) => reel?.getFreeSpinReelController())
      .filter(Boolean);
  }, [freeSpinReelRefs]);

  const getOverlayControllers = useCallback(() => {
    const reels = freeSpinOverlayReelRefs.current;
    if (!reels) return [];
    return reels
      .map((reel) => reel?.getFreeSpinOverlayReelController())
      .filter(Boolean);
  }, [freeSpinOverlayReelRefs]);

  /**
   * Compute and cache the master spine win animation duration.
   * Searches all overlay spines for the highest-priority symbol's win animation.
   */
  const getMasterSpineDuration = useCallback(() => {
    if (masterSpineDurationRef.current !== null) {
      return masterSpineDurationRef.current;
    }

    const SYMBOL_PRIORITY = { wild: 4, scatter: 3, hp: 2, mp: 1, lp: 0 };
    let bestPriority = -1;
    let bestDuration =
      FEATURES_CONFIG.WIN_ANIMATION_TIMING.SPINE_LOOP_DURATION;

    const overlayControllers = getOverlayControllers();
    for (const ctrl of overlayControllers) {
      if (!ctrl?.prePositionedSpines) continue;
      for (const [spineKey, spine] of ctrl.prePositionedSpines) {
        if (!spine?.state) continue;
        const parts = spineKey.split("_");
        const symId = parseInt(parts[1]);
        const symData = symbolData[symId];
        const priority = symData
          ? (SYMBOL_PRIORITY[symData.symbolPay] ?? 0)
          : 0;
        if (priority > bestPriority) {
          const animations = spine.skeleton?.data?.animations;
          if (animations) {
            const winAnim = animations.find((a) =>
              a.name.toLowerCase().includes("win"),
            );
            if (winAnim) {
              bestPriority = priority;
              bestDuration = winAnim.duration * 1000;
            }
          }
        }
      }
    }

    masterSpineDurationRef.current = bestDuration * (FEATURES_CONFIG.WIN_ANIMATION_TIMING.DURATION_SCALE || 1);
    return bestDuration;
  }, [getOverlayControllers]);

  const matchWinningLines = useCallback((winningLines, wins) => {
    const result = {};
    if (!Array.isArray(wins)) return result;
    for (let i = 0; i < wins.length; i++) {
      const win = wins[i];
      const { id, positions, symbol, win: winAmount } = win;
      if (!Array.isArray(positions)) continue;
      if (!result[i]) {
        result[i] = {
          line: id != null ? winningLines[id] : null,
          positions: [],
          symbol,
          win: winAmount,
          lineId: id,
          isScatter: !!win.isScatter,
        };
      }
      result[i].positions.push(...positions);
    }
    return result;
  }, []);

  /**
   * Reset all visual effects to clean state.
   */
  const resetAllEffects = useCallback(() => {
    if (dimReelSetRef.current) dimReelSetRef.current.undimReels();

    if (FEATURES_CONFIG.WIN_LINES.ENABLED && winLinesContainerRef.current) {
      winLinesContainerRef.current.hideWinLine();
    }

    const overlayControllers = getOverlayControllers();
    for (const ctrl of overlayControllers) {
      ctrl?.hideAllAnimations();
    }

    const reelControllers = getReelControllers();
    for (const ctrl of reelControllers) {
      for (let i = 0; i < GAME_LAYOUT.SYMBOLS_VISIBLE; i++) {
        ctrl?.showSymbolTexture?.(i);
      }
    }

    if (lineWinTextRef?.current) lineWinTextRef.current.hide();
    if (globalLineWinTextRef?.current) globalLineWinTextRef.current.hide();

    prevWinPosSetRef.current = null;
  }, [
    dimReelSetRef,
    winLinesContainerRef,
    lineWinTextRef,
    globalLineWinTextRef,
    getReelControllers,
    getOverlayControllers,
  ]);

  // ===================================================================
  //  showAllWinningSymbolsOnce — show-all phase (mirrors main game)
  // ===================================================================
  const showAllWinningSymbolsOnce = useCallback(
    async (winningData) => {
      const reelControllers = getReelControllers();
      const overlayControllers = getOverlayControllers();
      const allPositions = [];
      const lineIds = Object.keys(winningData);

      // Collect all unique positions
      for (const data of Object.values(winningData)) {
        for (const pos of data.positions) {
          const exists = allPositions.some(
            ([r, s]) => r === pos[0] && s === pos[1],
          );
          if (!exists) allPositions.push(pos);
        }
      }

      // Build winning positions per reel for targeted cleanup
      const winPositionsByReel = new Map();
      allPositions.forEach(([reelIndex, symbolIndex]) => {
        if (!winPositionsByReel.has(reelIndex)) {
          winPositionsByReel.set(reelIndex, new Set());
        }
        winPositionsByReel.get(reelIndex).add(symbolIndex);
      });

      // Hide non-winning overlay animations (keep winning ones)
      overlayControllers.forEach((ctrl, reelIndex) => {
        if (ctrl) {
          const exceptPositions = winPositionsByReel.get(reelIndex) || null;
          ctrl.hideAllAnimations(exceptPositions);
        }
      });

      // Show all reel textures
      for (const ctrl of reelControllers) {
        for (let i = 0; i < GAME_LAYOUT.SYMBOLS_VISIBLE; i++) {
          ctrl.showSymbolTexture?.(i);
        }
      }

      // Play win sound
      if (SOUND_NAME.basic_win) soundManager.play(SOUND_NAME.basic_win);

      // Start spine win animations on winning symbols
      for (const [reelIndex, symbolIndex] of allPositions) {
        const reelCtrl = reelControllers[reelIndex];
        const overlayCtrl = overlayControllers[reelIndex];
        if (!reelCtrl || !overlayCtrl) continue;

        const sprite = reelCtrl.spinningSprites?.[1 + symbolIndex];
        const symbolId = sprite?.currentSymbolId ?? sprite?.symbolId;
        if (symbolId !== undefined) {
          overlayCtrl.showWinAnimation(symbolIndex, symbolId);
        }
      }

      // Hide textures behind spines
      for (const [reelIndex, symbolIndex] of allPositions) {
        const reelCtrl = reelControllers[reelIndex];
        reelCtrl?.hideSymbolTexture?.(symbolIndex);
      }

      // Show all win lines
      if (FEATURES_CONFIG.WIN_LINES.ENABLED && winLinesContainerRef.current) {
        const regularLineIds = lineIds
          .map((key) => winningData[key]?.lineId)
          .filter((id) => id !== undefined && id !== null);
        if (regularLineIds.length > 0) {
          winLinesContainerRef.current.showMultipleWinLines(regularLineIds);
        }
      }

      // Show global win text
      if (globalLineWinTextRef?.current && currentRoundRef.current?.win) {
        globalLineWinTextRef.current.showWinAmount(currentRoundRef.current.win);
      }

      // Dim reels
      if (dimReelSetRef.current) dimReelSetRef.current.dimReels();

      // Wait for show-all duration (event-based timing)
      const masterDuration = getMasterSpineDuration();
      const loops = FEATURES_CONFIG.SHOW_ALL_WINS.LOOPS_FOR_SHOW_ALL || 1;

      await new Promise((resolve) => {
        const timeoutId = setTimeout(resolve, masterDuration * loops);
        currentTimeoutsRef.current.push(timeoutId);
      });

      // Dispatch counter finished and release animation lock
      dispatch(setCounterAnimationFinished(true));
      isAnimatingRef.current = false;

      return true;
    },
    [
      getReelControllers,
      getOverlayControllers,
      dimReelSetRef,
      winLinesContainerRef,
      globalLineWinTextRef,
      getMasterSpineDuration,
      dispatch,
    ],
  );

  // ===================================================================
  //  cycleThroughLines — per-line animation with diff-based transitions
  //  (mirrors main game's smooth transition logic)
  // ===================================================================
  const cycleThroughLines = useCallback(
    async (winningData, lineIds, isFirstEverCycle = false, startIndex = 0, onFirstLineComplete = null) => {
      const reelControllers = getReelControllers();
      const overlayControllers = getOverlayControllers();

      for (let index = startIndex; index < lineIds.length; index++) {
        if (!isAnimatingRef.current) break;

        const lineId = lineIds[index];
        currentLineIndexRef.current = index;
        const data = winningData[lineId];
        if (!data) continue;

        // Win line sound (only for numbered lines, scatter has null id)
        if (
          FEATURES_CONFIG.WIN_LINES.ENABLED &&
          SOUND_NAME.win_line &&
          data.lineId !== undefined &&
          data.lineId !== null
        ) {
          soundManager.play(SOUND_NAME.win_line);
        }

        // Hide previous line's text
        if (lineWinTextRef?.current) lineWinTextRef.current.hide();
        if (globalLineWinTextRef?.current) globalLineWinTextRef.current.hide();

        // Show this line's win amount
        if (data.win !== undefined && lineWinTextRef?.current) {
          lineWinTextRef.current.showWinAmount(data.win);
        }

        // Build current line positions with symbol IDs
        const currentLinePositions = data.positions.map(
          ([reelIndex, symbolIndex]) => {
            const reelCtrl = reelControllers[reelIndex];
            const sprite = reelCtrl?.spinningSprites?.[1 + symbolIndex];
            const symbolId = sprite?.currentSymbolId ?? sprite?.symbolId;
            return [reelIndex, symbolIndex, symbolId];
          },
        );

        // Pre-compute winning position Set for O(1) lookups
        const winPosSet = new Set(
          currentLinePositions.map(([r, s]) => `${r}_${s}`),
        );

        // Hide overlay animations EXCEPT current line's positions
        overlayControllers.forEach((ctrl) => {
          ctrl?.hideAllAnimationsExcept(currentLinePositions);
        });

        // Hide current win line before showing new one
        if (FEATURES_CONFIG.WIN_LINES.ENABLED && winLinesContainerRef.current) {
          winLinesContainerRef.current.hideWinLine();
        }

        // --- Diff-based texture transitions (key to smooth cycling) ---
        const prevSet = prevWinPosSetRef.current;
        const reelIndices = GAME_LAYOUT.REEL_INDICES;

        if (prevSet) {
          // Show textures for positions that WERE winning but no longer are
          for (const prevKey of prevSet) {
            if (!winPosSet.has(prevKey)) {
              const [reel, symbolIndex] = prevKey.split("_").map(Number);
              reelControllers[reel]?.showSymbolTexture?.(symbolIndex);
            }
          }
        } else {
          // First line — show all non-winning textures (full reset)
          for (let ri = 0; ri < reelIndices.length; ri++) {
            const reel = reelIndices[ri];
            const reelCtrl = reelControllers[reel];
            if (!reelCtrl) continue;
            for (let s = 0; s < GAME_LAYOUT.SYMBOLS_VISIBLE; s++) {
              if (!winPosSet.has(`${reel}_${s}`)) {
                reelCtrl.showSymbolTexture?.(s);
              }
            }
          }
        }



        // Sync win animations and hide textures for current winning positions
        for (let i = 0; i < data.positions.length; i++) {
          const [reelIndex, symbolIndex] = data.positions[i];
          const overlayCtrl = overlayControllers[reelIndex];
          const reelCtrl = reelControllers[reelIndex];
          if (!overlayCtrl || !reelCtrl) continue;

          // FreeSpinReel uses VISIBLE_START_INDEX = 1
          const sprite = reelCtrl.spinningSprites?.[1 + symbolIndex];
          if (sprite) {
            const actualSymbolId = sprite.currentSymbolId;
            if (actualSymbolId !== undefined) {
              overlayCtrl.syncWinAnimation(symbolIndex, actualSymbolId);
            }
          }

          reelCtrl.hideSymbolTexture?.(symbolIndex);
        }

        // Store current positions for next line's diff
        prevWinPosSetRef.current = winPosSet;

        // Show win line
        if (
          FEATURES_CONFIG.WIN_LINES.ENABLED &&
          winLinesContainerRef.current &&
          data.lineId !== undefined &&
          data.lineId !== null
        ) {
          winLinesContainerRef.current.showWinLine(data.lineId);
        }

        // Wait for line duration (event-based timing)
        const masterDuration = getMasterSpineDuration();
        const targetLoops =
          FEATURES_CONFIG.WIN_ANIMATION_TIMING.LOOPS_PER_LINE || 1;
        const lineDuration = masterDuration * targetLoops;

        // Second win_line sound at half duration
        if (
          FEATURES_CONFIG.WIN_LINES.ENABLED &&
          SOUND_NAME.win_line &&
          data.lineId !== undefined &&
          data.lineId !== null
        ) {
          const secondPlayTimeout = setTimeout(() => {
            soundManager.play(SOUND_NAME.win_line);
          }, lineDuration / 2);
          currentTimeoutsRef.current.push(secondPlayTimeout);
        }

        await new Promise((resolve) => {
          const timeoutId = setTimeout(resolve, lineDuration);
          currentTimeoutsRef.current.push(timeoutId);
        });

        // After first line, fire unblock callback
        if (index === 0 && onFirstLineComplete) {
          onFirstLineComplete();
          onFirstLineComplete = null;
        }


      }
    },
    [
      getReelControllers,
      getOverlayControllers,
      winLinesContainerRef,
      lineWinTextRef,
      globalLineWinTextRef,
      getMasterSpineDuration,
    ],
  );

  // Stable refs for async use
  const showAllWinningSymbolsOnceRef = useRef(null);
  showAllWinningSymbolsOnceRef.current = showAllWinningSymbolsOnce;

  const startWinningLinesAnimationRef = useRef(null);

  // ===================================================================
  //  startWinningLinesAnimation — entry point for line cycling
  // ===================================================================
  const startWinningLinesAnimation = useCallback(
    async (winningData) => {
      if (isAnimatingRef.current) return;

      const lineIds = Object.keys(winningData);

      isAnimatingRef.current = true;
      clearAllTimeouts();

      if (globalLineWinTextRef?.current) globalLineWinTextRef.current.hide();

      // Clear previous win positions for clean first-line diff
      prevWinPosSetRef.current = null;

      if (dimReelSetRef.current) dimReelSetRef.current.dimReels();

      winningDataRef.current = winningData;
      lineIdsRef.current = lineIds.slice();
      currentLineIndexRef.current = 0;

      const runCycle = async () => {
        try {
          // Play through all lines once
          await cycleThroughLines(
            winningData,
            lineIds,
            isFirstAnimationRef.current,
            0,
            // unblockCallback — fires during first line for counter display
            isFirstAnimationRef.current
              ? () => dispatchRef.current(setCounterAnimationFinished(true))
              : null,
          );

          isFirstAnimationRef.current = false;
          currentLineIndexRef.current = 0;

          // Re-show last line's win text and win line so they stay visible
          // during the covering cascade (the winline_digit spine fade-out
          // has already hidden them by the time the loop exits).
          const lastLineId = lineIds[lineIds.length - 1];
          const lastData = winningData[lastLineId];
          if (lastData) {
            if (lastData.win !== undefined && lineWinTextRef?.current) {
              lineWinTextRef.current.showWinAmount(lastData.win);
            }
            if (
              FEATURES_CONFIG.WIN_LINES.ENABLED &&
              winLinesContainerRef.current &&
              lastData.lineId !== undefined &&
              lastData.lineId !== null
            ) {
              winLinesContainerRef.current.showWinLine(lastData.lineId);
            }
          }

          // All lines played — wait for user to return if tab is inactive
          // before signaling that covering can begin
          isAnimatingRef.current = false;

          if (!activityRef.current) {
            await new Promise((resolve) => {
              const check = setInterval(() => {
                if (activityRef.current) {
                  clearInterval(check);
                  resolve();
                }
              }, 100);
            });
          }

          dispatchRef.current(setFreeSpinCycleComplete(true));
        } catch {
          // Interrupted
        }
      };

      runCycle();
    },
    [clearAllTimeouts, cycleThroughLines, dimReelSetRef, globalLineWinTextRef, lineWinTextRef, winLinesContainerRef],
  );

  startWinningLinesAnimationRef.current = startWinningLinesAnimation;

  // ===================================================================
  //  MAIN EFFECT — triggers win processing when freeSpinComplete fires
  // ===================================================================
  useEffect(() => {
    if (!freeSpinActive || !freeSpinComplete || !hasSpunRef.current) return;
    if (isAnimatingRef.current || animationCompletedRef.current) return;
    if (!hasWins || !winningLines) return;

    const reelControllers = getReelControllers();
    if (reelControllers.length === 0) return;

    // Dedup animation
    const wins = currentRound?.wins;
    const roundId = currentRound?.id || "unknown";
    const winsHash = Array.isArray(wins) ? JSON.stringify(wins) : "";
    const animId = `${roundId}-${winsHash}`;
    if (animatedRoundsRef.current.has(animId)) {
      animationCompletedRef.current = true;
      dispatch(setFreeSpinCycleComplete(true));
      return;
    }
    animatedRoundsRef.current.add(animId);

    // Start
    isAnimatingRef.current = true;
    dispatch(setCounterAnimationStarted(true));
    dispatch(setCriticalAnimationPlaying(true));

    const winningData = matchWinningLines(winningLines, currentRound.wins);
    if (!winningData || Object.keys(winningData).length === 0) {
      isAnimatingRef.current = false;
      dispatch(setFreeSpinCycleComplete(true));
      return;
    }

    const shouldShowAllWins = FEATURES_CONFIG.SHOW_ALL_WINS.ENABLED;

    (async () => {
      // Delay before starting win animations — let symbols settle after reveal
      const winLinesDelay = FEATURES_CONFIG.WIN_ANIMATION_TIMING.FREE_SPIN_WIN_LINES_DELAY;
      if (winLinesDelay > 0) {
        await new Promise((r) => setTimeout(r, winLinesDelay));
      }

      // Guard: check if we were reset during the delay
      if (!isAnimatingRef.current) return;

      dispatch(setCriticalAnimationPlaying(false));

      const isAnimatedWinLocal = currentRound?.isAnimatedWin || false;
      dispatch(setIsAnimatedWin(isAnimatedWinLocal));
      dispatch(setCounterAnimationStarted(true));

      // If animated win (big win, mega win, etc.), block popups and signal win animation
      if (isAnimatedWinLocal) {
        dispatch(closeAllPopups());
        dispatch(setSymbolPopupBlocked(true));
        dispatch(setWinAnimationsPlaying(true));
      }

      let shouldProceed = true;

      if (shouldShowAllWins) {
        shouldProceed =
          await showAllWinningSymbolsOnceRef.current(winningData);
      }

      if (shouldProceed) {
        if (!shouldShowAllWins) {
          isAnimatingRef.current = false;
        }
        startWinningLinesAnimationRef.current(winningData);
      } else {
        if (
          FEATURES_CONFIG.WIN_LINES.ENABLED &&
          winLinesContainerRef.current
        ) {
          winLinesContainerRef.current.hideWinLine();
        }
        if (globalLineWinTextRef?.current) globalLineWinTextRef.current.hide();
        if (lineWinTextRef?.current) lineWinTextRef.current.hide();
      }

      if (!shouldShowAllWins) {
        if (SOUND_NAME.basic_win) soundManager.play(SOUND_NAME.basic_win);
      }
    })();

    return () => clearAllTimeouts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [freeSpinComplete, freeSpinActive, dispatch]);

  // ===================================================================
  //  SKIP DETECTION — user pressed skip while lines are still cycling
  // ===================================================================
  const freeSpinCycleComplete = useSelector(
    (state) => state.animations.freeSpinCycleComplete,
  );

  useEffect(() => {
    if (!freeSpinCycleComplete || !freeSpinActive) return;

    // Skip: stop the line cycle timer only.
    // Dim, win lines, and win text stay visible — they persist during
    // the covering cascade and are cleaned up by FreeSpinControlContext
    // after all masks complete.
    clearAllTimeouts();
    isAnimatingRef.current = false;
  }, [freeSpinCycleComplete, freeSpinActive, clearAllTimeouts]);

  // ===================================================================
  //  RESET EFFECT — new round starting (freeSpinSpinning)
  // ===================================================================
  useEffect(() => {
    if (!freeSpinSpinning) return;

    hasSpunRef.current = true;

    clearAllTimeouts();
    isAnimatingRef.current = false;
    animationCompletedRef.current = false;
    isFirstAnimationRef.current = true;
    masterSpineDurationRef.current = null;
    prevWinPosSetRef.current = null;
    winningDataRef.current = null;
    lineIdsRef.current = [];
    currentLineIndexRef.current = 0;

    resetAllEffects();
    dispatch(setFreeSpinCycleComplete(false));
  }, [freeSpinSpinning, clearAllTimeouts, resetAllEffects, dispatch]);

  // ===================================================================
  //  DEACTIVATION — reset when leaving free spin mode
  // ===================================================================
  useEffect(() => {
    if (!freeSpinActive) {
      hasSpunRef.current = false;
      clearAllTimeouts();
      isAnimatingRef.current = false;
      animationCompletedRef.current = false;
      isFirstAnimationRef.current = true;
      masterSpineDurationRef.current = null;
      animatedRoundsRef.current.clear();
    }
  }, [freeSpinActive, clearAllTimeouts]);

  // ===================================================================
  //  CLEANUP
  // ===================================================================
  useEffect(() => {
    return () => {
      clearAllTimeouts();
      isAnimatingRef.current = false;
    };
  }, [clearAllTimeouts]);
};

export default useFreeSpinWinController;
